import React from 'react';
import { defineMessages, useIntl } from 'react-intl';
import { Box } from '@lifeomic/chroma-react/components/Box';
import { Text } from '@lifeomic/chroma-react/components/Text';
import { makeStyles } from '@lifeomic/chroma-react/styles';
import { Tooltip } from '@lifeomic/chroma-react/components/Tooltip';
import { MemberOnboardingData } from '../index';
import { Divider } from '@lifeomic/chroma-react/components/Divider';
import {
  AppointmentSnapshot,
  QuestionnaireResponseSnapshot,
  formatUpcomingAppointment,
} from './OnboardingSnapshotTable';

const messages = defineMessages({
  name: {
    id: 'OnboardingSnapshotList.name',
    defaultMessage: 'Name',
  },
  tierLabel: {
    id: 'OnboardingSnapshotList.tierLabel',
    defaultMessage: 'Tier',
  },
  nextAppointment: {
    id: 'OnboardingSnapshotList.nextAppointment',
    defaultMessage: 'Next Appointment',
  },
  noUpcomingAppointments: {
    id: 'OnboardingSnapshotList.noUpcomingAppointments',
    defaultMessage: 'No Upcoming Appointments',
  },
  inPerson: {
    id: 'OnboardingSnapshotList.inPerson',
    defaultMessage: 'In-Person',
  },
  virtual: {
    id: 'OnboardingSnapshotList.virtual',
    defaultMessage: 'Virtual',
  },
  consents: {
    id: 'OnboardingSnapshotList.consents',
    defaultMessage: 'Consents',
  },
  surveys: {
    id: 'OnboardingSnapshotList.surveys',
    defaultMessage: 'Surveys',
  },
  nursePrescreening: {
    id: 'OnboardingSnapshotList.nursePrescreening',
    defaultMessage: 'Prescreening',
  },
  ppc: {
    id: 'OnboardingSnapshotList.ppc',
    defaultMessage: 'PPC',
  },
  precisionDiagnostics: {
    id: 'OnboardingSnapshotList.precisionDiagnostics',
    defaultMessage: 'Precision Diagnostics',
  },
  lastAssigned: {
    id: 'OnboardingSnapshotList.lastAssigned',
    defaultMessage: 'Last Assigned',
  },
  lastCompleted: {
    id: 'OnboardingSnapshotList.lastCompleted',
    defaultMessage: 'Last Completed',
  },
  noneAssigned: {
    id: 'OnboardingSnapshotList.noneAssigned',
    defaultMessage: 'None Assigned in the last 13 months',
  },
  provider: {
    id: 'OnboardingSnapshotList.provider',
    defaultMessage: 'Provider',
  },
  careCoordinator: {
    id: 'OnboardingSnapshotList.careCoordinator',
    defaultMessage: 'Care Coordinator',
  },
});

const useStyles = makeStyles(theme => ({
  list: {
    display: 'grid',
    gap: `0 ${theme.spacing(1.5)}`,
    gridTemplateColumns: '175px 1fr',
  },
  listItemLabel: {
    color: theme.palette.text.hint,
    fontWeight: theme.typography.fontWeightBold,
    minWidth: theme.pxToRem(175),
  },
  capitalize: {
    textTransform: 'capitalize',
  },
  divider: {
    gridColumn: '-1 / 1',
  },
  memberName: {
    lineHeight: 1.35,
  },
  locationIcon: {
    color: theme.palette.text.hint,
    minWidth: theme.pxToRem(20),
    minHeight: theme.pxToRem(20),
    width: theme.pxToRem(20),
    height: theme.pxToRem(20),
  },
  apptText: {
    lineHeight: 1.35,
  },
  statusIcon: {
    minWidth: theme.pxToRem(20),
    minHeight: theme.pxToRem(20),
    width: theme.pxToRem(20),
    height: theme.pxToRem(20),
  },
  statusRow: {
    alignItems: 'center',
  },
}));

type OnboardingSnapshotListProps = {
  member: MemberOnboardingData['member'];
  snapshots: MemberOnboardingData['snapshots'];
};

export const OnboardingSnapshotList: React.FC<OnboardingSnapshotListProps> = ({
  member,
  snapshots,
}) => {
  const styles = useStyles();
  const { formatMessage } = useIntl();

  const { consents, surveys, prescreening, ppc, diagnostics } = snapshots;

  const nextAppointment = formatUpcomingAppointment(
    snapshots.nextAppointment.appointment,
  );

  return (
    <Box className={styles.list} direction="column">
      <Text size="subbody" className={styles.listItemLabel}>
        {formatMessage(messages.tierLabel)}
      </Text>
      <Box direction="column" gap={0.25}>
        {member.tier && (
          <Box align="center" gap={0.25}>
            <Text size="subbody">{member.tier.toUpperCase()}</Text>
          </Box>
        )}
      </Box>

      <Divider className={styles.divider} />

      <Text size="subbody" className={styles.listItemLabel}>
        {formatMessage(messages.provider)}
      </Text>
      <Box direction="column" gap={0.25}>
        <Box align="center" gap={0.25}>
          <Text size="subbody" className={styles.capitalize}>
            {member.providerName}
          </Text>
        </Box>
      </Box>

      <Divider className={styles.divider} />

      <Text size="subbody" className={styles.listItemLabel}>
        {formatMessage(messages.careCoordinator)}
      </Text>
      <Box direction="column" gap={0.25}>
        <Box align="center" gap={0.25}>
          <Text size="subbody">{member.careCoordinatorName}</Text>
        </Box>
      </Box>
      <Divider className={styles.divider} />

      {/* Next Appointment */}
      <Text size="subbody" className={styles.listItemLabel}>
        {formatMessage(messages.nextAppointment)}
      </Text>
      {!nextAppointment ? (
        <Text size="subbody">
          {formatMessage(messages.noUpcomingAppointments)}
        </Text>
      ) : (
        <Tooltip
          title={`${
            nextAppointment.description
              ? `${nextAppointment.description} - `
              : ''
          } ${
            nextAppointment.locationTypeCode === 'in-person'
              ? formatMessage(messages.inPerson)
              : formatMessage(messages.virtual)
          }`}
        >
          <Box direction="column" gap={0.5}>
            <Text size="subbody">{nextAppointment.description}</Text>
            <Box align="center" gap={1.5}>
              {nextAppointment.locationIcon && (
                <nextAppointment.locationIcon className={styles.locationIcon} />
              )}
              <Box direction="column">
                <Text className={styles.apptText} size="caption">
                  {nextAppointment.date}
                </Text>
                {nextAppointment.time && (
                  <Text className={styles.apptText} size="caption">
                    {nextAppointment.time}
                  </Text>
                )}
              </Box>
            </Box>
          </Box>
        </Tooltip>
      )}
      <Divider className={styles.divider} />

      {/* Consents */}
      <Text size="subbody" className={styles.listItemLabel}>
        {formatMessage(messages.consents)}
      </Text>

      <QuestionnaireResponseSnapshot status={consents.status} />
      <Divider className={styles.divider} />

      <Text size="subbody" className={styles.listItemLabel}>
        {formatMessage(messages.lastAssigned)}
      </Text>

      <Text size="subbody" className={styles.listItemLabel}>
        {snapshots.consents.hint.lastAssigned ||
          formatMessage(messages.noneAssigned)}
      </Text>

      <Divider className={styles.divider} />

      <Text size="subbody" className={styles.listItemLabel}>
        {formatMessage(messages.lastCompleted)}
      </Text>

      <Text size="subbody" className={styles.listItemLabel}>
        {snapshots.consents.hint.lastCompleted ||
          formatMessage(messages.noneAssigned)}
      </Text>

      <Divider className={styles.divider} />

      {/* Surveys */}
      <Text size="subbody" className={styles.listItemLabel}>
        {formatMessage(messages.surveys)}
      </Text>
      <QuestionnaireResponseSnapshot status={surveys.status} />
      <Divider className={styles.divider} />

      <Text size="subbody" className={styles.listItemLabel}>
        {formatMessage(messages.lastAssigned)}
      </Text>

      <Text size="subbody" className={styles.listItemLabel}>
        {snapshots.surveys.hint.lastAssigned ||
          formatMessage(messages.noneAssigned)}
      </Text>

      <Divider className={styles.divider} />

      <Text size="subbody" className={styles.listItemLabel}>
        {formatMessage(messages.lastCompleted)}
      </Text>

      <Text size="subbody" className={styles.listItemLabel}>
        {snapshots.surveys.hint.lastCompleted ||
          formatMessage(messages.noneAssigned)}
      </Text>

      <Divider className={styles.divider} />

      {/* Nurse Prescreening */}
      <Text size="subbody" className={styles.listItemLabel}>
        {formatMessage(messages.nursePrescreening)}
      </Text>
      <AppointmentSnapshot
        appointment={prescreening.appointment}
        type="prescreening"
        status={prescreening.status}
        direction="row"
      />
      <Divider className={styles.divider} />

      {/* PPC */}
      <Text size="subbody" className={styles.listItemLabel}>
        {formatMessage(messages.ppc)}
      </Text>
      <AppointmentSnapshot
        appointment={ppc.appointment}
        type="ppc"
        status={ppc.status}
        direction="row"
      />
      <Divider className={styles.divider} />

      {/* Precision Diagnostics */}
      <Text size="subbody" className={styles.listItemLabel}>
        {formatMessage(messages.precisionDiagnostics)}
      </Text>
      <AppointmentSnapshot
        appointment={diagnostics.appointment}
        type="diagnostics"
        status={diagnostics.status}
        direction="row"
      />
      <Divider className={styles.divider} />
    </Box>
  );
};
